import type { TypographyProps } from "@mui/material";
import { Box, Typography, styled } from "@mui/material";
import type { ComponentPropsWithRef } from "react";
import { NavLink } from "react-router";

import { Tooltip as UnstyledTooltip } from "@citrine/atoms/tooltip";
import type { BranchNodeType } from "@citrine/resources/branch";
import { DEFAULT_SKEW, DEG_TO_RAD } from "@citrine/utils/skew";

const INDICATOR_BORDER = 0.125; // 1px / 8px

const backgroundColor = "--step-background-color";
const borderColor = "--step-border-color";

interface StepProps extends ComponentPropsWithRef<typeof Box> {
  complete?: boolean;
  nodeType: BranchNodeType;
}
export const Step = styled(Box, {
  shouldForwardProp: (
    propName: keyof StepProps,
  ): propName is keyof ComponentPropsWithRef<typeof Box> =>
    !["complete", "nodeType"].includes(propName),
})<StepProps>(({ theme, complete, nodeType }) => ({
  [backgroundColor]: complete
    ? theme.palette[nodeType].main
    : theme.palette[nodeType].light,
  [borderColor]: theme.palette[nodeType].dark,
  color: "inherit",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "space-between",
  position: "relative",
  textAlign: "center",
  textDecoration: "none",
  width: "25%",

  "&:last-child::after": {
    backgroundColor: `var(${backgroundColor})`,
    border: `${theme.spacing(INDICATOR_BORDER)} solid var(${borderColor})`,
    borderLeft: "none",
    bottom: 0,
    content: "''",
    height: theme.spacing(theme.layout.BRANCH_INDICATOR_HEIGHT),
    position: "absolute",
    right: 0,
    width: theme.spacing(theme.layout.BRANCH_INDICATOR_HEIGHT),
  },
}));
export const StepLink = Step.withComponent(NavLink);

export const TitleContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  color: theme.palette.common.white,
  display: "flex",
  flexDirection: "row",
  flexShrink: 0,
  fontWeight: theme.typography.fontWeightMedium,
  gap: theme.spacing(0.5),
  justifyContent: "center",
  margin: "auto",
  padding: theme.spacing(0, 1.5, 0, 5),
  width: "100%",
}));

export const Title = styled(Typography)<TypographyProps>({ opacity: 0.6 });

export const Tooltip = styled(UnstyledTooltip)({
  flex: "0 1 max-content",
  width: "inherit",
}) as typeof UnstyledTooltip;

export const LabelContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(0.5),
  justifyContent: "center",
  opacity: 0.84,
  overflow: "hidden",
}));

export const Label = styled(Typography)<TypographyProps>({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const Version = styled(Typography)<TypographyProps>({ flexShrink: 0 });

// the colored indicator at the bottom of the tab

export const Indicator = styled("div")(({ theme }) => ({
  backgroundColor: `var(${backgroundColor})`,
  border: `${theme.spacing(INDICATOR_BORDER)} solid var(${borderColor})`,
  flexShrink: 0,
  height: theme.spacing(theme.layout.BRANCH_INDICATOR_HEIGHT),
  position: "relative",
  transform: `skew(${-DEFAULT_SKEW}rad)`,
  transformOrigin: "bottom",

  // this is the pointy arrow thingy
  ".active &::before": {
    backgroundColor: `var(${backgroundColor})`,
    border: `solid var(${borderColor})`,
    borderWidth: theme.spacing(0, INDICATOR_BORDER, INDICATOR_BORDER, 0),
    bottom: -2,
    content: "''",
    height: theme.spacing(theme.layout.BRANCH_INDICATOR_HEIGHT),
    left: "50%",
    position: "absolute",
    transform: `skew(${DEFAULT_SKEW}rad) translate(-50%, ${theme.spacing(
      theme.layout.BRANCH_INDICATOR_HEIGHT / 2 +
        INDICATOR_BORDER * (Math.sin(45 * DEG_TO_RAD) - 2),
    )}) rotate(45deg)`,
    width: theme.spacing(theme.layout.BRANCH_INDICATOR_HEIGHT),
  },
}));
